import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import Collection from "@kaspernj/api-maker/build/collection.js"
import modelClassRequire from "@kaspernj/api-maker/build/model-class-require.js"

const modelClassData = {
  "attributes": {
    "created_at": {
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      },
      "name": "created_at",
      "selected_by_default": null,
      "translated": null
    },
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      },
      "name": "id",
      "selected_by_default": null,
      "translated": null
    },
    "school_class_id": {
      "column": {
        "default": null,
        "name": "school_class_id",
        "null": true,
        "type": null
      },
      "name": "school_class_id",
      "selected_by_default": null,
      "translated": null
    },
    "updated_at": {
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      },
      "name": "updated_at",
      "selected_by_default": null,
      "translated": null
    }
  },
  "className": "Team",
  "collectionKey": "teams",
  "collectionName": "teams",
  "i18nKey": "team",
  "camelizedLower": "team",
  "name": "Team",
  "nameDasherized": "team",
  "pluralName": "teams",
  "ransackable_associations": [
    {
      "className": "TeamSurveyStepElementAnswer",
      "collectionName": "team_survey_step_element_answers",
      "foreignKey": "team_survey_step_element_id",
      "name": "answers",
      "macro": "has_many",
      "resource_name": "TeamSurveyStepElementAnswer",
      "through": "team_surveys"
    },
    {
      "className": "Team::Audit",
      "collectionName": "team_audits",
      "foreignKey": "team_id",
      "name": "audits",
      "macro": "has_many",
      "resource_name": "TeamAudit",
      "through": null
    },
    {
      "className": "Team::Audit",
      "collectionName": "team_audits",
      "foreignKey": "team_id",
      "name": "create_audit",
      "macro": "has_one",
      "resource_name": "TeamAudit",
      "through": null
    },
    {
      "className": "SchoolClass",
      "collectionName": "school_classes",
      "foreignKey": "school_class_id",
      "name": "school_class",
      "macro": "belongs_to",
      "resource_name": "SchoolClass",
      "through": null
    },
    {
      "className": "SchoolClassCourse",
      "collectionName": "school_class_courses",
      "foreignKey": "team_id",
      "name": "school_class_course",
      "macro": "has_one",
      "resource_name": "SchoolClassCourse",
      "through": null
    },
    {
      "className": "TeamSurvey",
      "collectionName": "team_surveys",
      "foreignKey": "team_id",
      "name": "team_surveys",
      "macro": "has_many",
      "resource_name": "TeamSurvey",
      "through": null
    },
    {
      "className": "TeamUser",
      "collectionName": "team_users",
      "foreignKey": "team_id",
      "name": "team_users",
      "macro": "has_many",
      "resource_name": "TeamUser",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "school_class_id",
      "column": {
        "default": null,
        "name": "school_class_id",
        "null": true,
        "type": null
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    }
  ],
  "ransackable_scopes": [],
  "relationships": [
    {
      "className": "SchoolClass",
      "collectionName": "school_classes",
      "foreignKey": "school_class_id",
      "name": "school_class",
      "macro": "belongs_to",
      "resource_name": "SchoolClass",
      "through": null
    },
    {
      "className": "TeamUser",
      "collectionName": "team_users",
      "foreignKey": "team_id",
      "name": "team_users",
      "macro": "has_many",
      "resource_name": "TeamUser",
      "through": null
    }
  ],
  "paramKey": "team",
  "primaryKey": "id"
}

/** Frontend model for Team. */
class Team extends BaseModel {
  /** @returns {Record<string, any>} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {string} */
  createdAt() {
    return this.readAttributeUnderscore("created_at")
  }

  /** @returns {boolean} */
  hasCreatedAt() {
    const value = this.createdAt()

    return this._isPresent(value)
  }

  /** @returns {any} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {any | null} */
  schoolClassId() {
    return this.readAttributeUnderscore("school_class_id")
  }

  /** @returns {boolean} */
  hasSchoolClassId() {
    const value = this.schoolClassId()

    return this._isPresent(value)
  }

  /** @returns {string} */
  updatedAt() {
    return this.readAttributeUnderscore("updated_at")
  }

  /** @returns {boolean} */
  hasUpdatedAt() {
    const value = this.updatedAt()

    return this._isPresent(value)
  }

  /** @returns {import("./school-class.js").default | null} */
  schoolClass() {
    return this._readBelongsToReflection({reflectionName: "school_class"})
  }

  /** @returns {Promise<import("./school-class.js").default | null>} */
  loadSchoolClass() {
    if (!("schoolClassId" in this)) throw new Error("Foreign key method wasn't defined: schoolClassId")

    const id = this.schoolClassId()
    const modelClass = modelClassRequire("SchoolClass")
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {reflectionName: "school_class", model: this, modelClass},
      {ransack}
    )
  }

  /** @returns {import("@kaspernj/api-maker/build/collection.js").default<typeof import("./team-user.js").default>} */
  teamUsers() {
    if (!("id" in this)) throw new Error("No such primary key method: id")

    const modelClass = modelClassRequire("TeamUser")

    const ransack = {}

    ransack["team_id_eq"] = this.id()

    return new Collection(
      {
        reflectionName: "team_users",
        model: this,
        modelName: "TeamUser",
        modelClass
      },
      {ransack}
    )
  }

  /** @returns {Promise<Array<import("./team-user.js").default>>} */
  loadTeamUsers() {
    if (!("id" in this)) throw new Error("No such primary key method: id")

    const modelClass = modelClassRequire("TeamUser")

    const ransack = {}

    ransack["team_id_eq"] = this.id()

    return this._loadHasManyReflection(
      {
        reflectionName: "team_users",
        model: this,
        modelClass
      },
      {ransack}
    )
  }
}

export default Team
